import {
  Button,
  Checkbox,
  FormField,
  Link,
  Spacer,
  SvgArrowBack,
  SvgArrowForward,
  Text,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';

import { INVISIBLE_SITE_KEY } from 'constants/captcha';
import {
  EMAIL_LOGIN_BUTTON_CLICKED,
  EMAIL_SIGNUP_BUTTON_CLICKED,
  EMAIL_SIGNUP_FORM_TOGGLED,
  SIGNUP_CLICKED_TERMS,
  SIGNUP_TOGGLE_TERMS_CHECKBOX,
} from 'constants/events';
import { ENTER } from 'constants/keyCodes';
import { TERMS } from 'constants/routes';
import * as analytics from 'utilities/analytics';

import {
  updateLoginEmailError,
  updateLoginPasswordError,
  updateLoginPlainError,
} from '../../../actions/login';
import { useUrlQueryParam } from '../../../contexts/UrlQueryParamContext';
import {
  checkIfUserExists,
  logInUserCreator,
  signUpUserCreator,
} from '../../../creators/login';
import { isMobile } from '../../../utilities/device';
import {
  email as emailValiate,
  hasPasswordError,
  invalidUserFirstName,
} from '../../../../../shared/validate';

import './styles.less';

// this is the compact form that shows up with the connect accounts
function CompactEmailForm(props) {
  const {
    email,
    setEmail,
    password,
    setPassword,
    isSignUp,
    errorStr,
    overlayMode,
    redirectIsWorkspaceOnboarding,
  } = props;

  const buttonHandler = isSignUp ? props.expansionHandler : props.signInHandler;

  const orgToken = useUrlQueryParam(state => state.orgToken);

  let signupString = 'Create Free Account';

  if (redirectIsWorkspaceOnboarding || orgToken) {
    signupString = 'Create account';
  } else if (overlayMode) {
    signupString = 'Signup with email';
  }

  return (
    <>
      <FormField
        className="loom-email-form-field"
        label="Email"
        labelFor="email"
        errorMessage={isSignUp && errorStr}
      >
        <TextInput
          id="email"
          placeholder="Enter your email..."
          value={email}
          onChange={setEmail}
          type="email"
          onKeyDown={e => {
            if (e.keyCode === ENTER) {
              buttonHandler();
            }
          }}
        />
      </FormField>

      {!isSignUp && (
        <>
          <Spacer top={1} />
          <FormField
            className="loom-email-form-field"
            label="Password"
            labelFor="password"
            errorMessage={errorStr}
          >
            <TextInput
              id="password"
              placeholder="•••••••••••"
              value={password}
              onChange={setPassword}
              type="password"
              onKeyDown={e => {
                if (e.keyCode === ENTER) {
                  buttonHandler();
                }
              }}
            />
          </FormField>
        </>
      )}
      <Spacer top={2} />
      <Button
        size={overlayMode ? 'medium' : 'large'}
        variant="primary"
        hasFullWidth={true}
        onClick={buttonHandler}
      >
        {isSignUp ? signupString : 'Sign In'}
      </Button>
    </>
  );
}

function EmailSignupForm(props) {
  const dispatch = useDispatch();
  const recaptchaRef = useRef();

  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [acceptedTC, setAcceptedTC] = useState(false);
  const [captchaSuccess, setCaptchaSuccess] = useState(false);

  const plainError = useSelector(state => state.login.plain_error);
  const passwordError = useSelector(state => state.login.password_error);
  const emailError = useSelector(state => state.login.email_error);
  const isLoading = useSelector(state => state.login.is_fetching);

  const {
    emailAddress,
    expanded,
    isSignUp,
    overlayMode,
    redirectIsWorkspaceOnboarding,
  } = props;

  const errorStr = plainError || passwordError || emailError;

  const [email, setEmail] = useState(emailAddress || '');

  const onChangeHandler = handler => e => {
    handler(e.target.value);

    dispatch(updateLoginEmailError(null));
    dispatch(updateLoginPasswordError(null));
    dispatch(updateLoginPlainError(null));
  };

  const buttonSize = overlayMode ? 'medium' : 'large';

  // this is the mini version of the form that shows up
  // before the larger signup form below
  if (!expanded) {
    return (
      <CompactEmailForm
        overlayMode={overlayMode}
        redirectIsWorkspaceOnboarding={redirectIsWorkspaceOnboarding}
        errorStr={plainError || passwordError || emailError}
        isSignUp={isSignUp}
        email={email}
        setEmail={onChangeHandler(setEmail)}
        password={password}
        setPassword={onChangeHandler(setPassword)}
        expansionHandler={async () => {
          const userExists = await dispatch(checkIfUserExists(email));

          if (userExists) {
            return;
          }

          props.expansionHandler(true);

          analytics.track(EMAIL_SIGNUP_FORM_TOGGLED, {
            action: 'opened',
          });
        }}
        signInHandler={() => {
          dispatch(logInUserCreator(email, password, window.location.href));

          analytics.track(EMAIL_LOGIN_BUTTON_CLICKED);
        }}
      />
    );
  }

  const allFieldsFilled =
    email && password && firstName && lastName && confirmPassword;

  return (
    <div
      className={classNames(
        'loom-signup-component-email-form',
        overlayMode && 'overlay-mode'
      )}
    >
      {!overlayMode && (
        <div className="loom-signup-email-form-back-button">
          <TextButton
            icon={<SvgArrowBack />}
            onClick={() => {
              props.expansionHandler(false);

              analytics.track(EMAIL_SIGNUP_FORM_TOGGLED, {
                action: 'closed',
              });
            }}
          >
            Back
          </TextButton>
        </div>
      )}
      <Text size="xlarge" fontWeight="semiBold">
        What is your name?
      </Text>
      <Spacer top={1} />
      <Text>This is the name that will appear on your videos.</Text>
      <Spacer top={overlayMode ? 3 : 4} />
      <FormField
        className="loom-email-form-field"
        label="Email"
        labelFor="email"
        errorMessage={emailError}
      >
        <TextInput
          id="email"
          type="email"
          placeholder="Juniper@loom.com"
          value={email}
          onChange={onChangeHandler(setEmail)}
        />
      </FormField>
      <Spacer top={2} />
      <div className="loom-signup-component-email-form-name">
        <FormField
          className="loom-email-form-field"
          label="First Name"
          labelFor="first-name"
        >
          <TextInput
            id="first-name"
            placeholder="Juniper"
            value={firstName}
            onChange={onChangeHandler(setFirstName)}
          />
        </FormField>
        <Spacer left={1} right={1} top={2} />
        <FormField
          className="loom-email-form-field"
          label="Last Name"
          labelFor="last-name"
        >
          <TextInput
            id="last-name"
            placeholder="Fruite"
            value={lastName}
            onChange={onChangeHandler(setLastName)}
          />
        </FormField>
      </div>
      <Spacer top={2} />
      <FormField
        className="loom-email-form-field"
        label="Password"
        labelFor="password"
      >
        <TextInput
          id="password"
          type="password"
          placeholder="•••••••••••"
          value={password}
          onChange={onChangeHandler(setPassword)}
        />
      </FormField>
      <Spacer top={2} />
      <FormField
        className="loom-email-form-field"
        label="Confirm Password"
        labelFor="confirm-password"
        errorMessage={passwordError || plainError}
      >
        <TextInput
          id="confirm-password"
          type="password"
          placeholder="•••••••••••"
          value={confirmPassword}
          onChange={onChangeHandler(setConfirmPassword)}
        />
      </FormField>
      <Spacer top={2} />
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={INVISIBLE_SITE_KEY}
        size="invisible"
        onChange={() => {
          // https://developers.google.com/recaptcha/docs/invisible#render_param
          // this is only invoked if the captcha is successful
          setCaptchaSuccess(true);
        }}
        onExpired={() => {
          recaptchaRef.current.reset();
        }}
        onErrored={() => {
          recaptchaRef.current.reset();
        }}
      />
      <div className="loom-signup-component-email-form-tc">
        <Checkbox
          onChange={() => {
            setAcceptedTC(!acceptedTC);

            if (!acceptedTC) {
              recaptchaRef.current.execute();
            }

            analytics.track(SIGNUP_TOGGLE_TERMS_CHECKBOX, {
              checked: !acceptedTC,
            });
          }}
          isChecked={acceptedTC}
        />
        <Spacer left={1} />
        <Text>
          {'I agree to Loom’s '}
          <Link
            href={TERMS}
            target="_blank"
            onClick={() => {
              analytics.track(SIGNUP_CLICKED_TERMS);
            }}
          >
            terms and conditions
          </Link>
        </Text>
      </div>
      <Spacer top={4} />
      <div className="loom-signup-component-email-form-button">
        {overlayMode && (
          <>
            <Button
              onClick={() => {
                props.expansionHandler(false);

                analytics.track(EMAIL_SIGNUP_FORM_TOGGLED, {
                  action: 'closed',
                });
              }}
            >
              Go Back
            </Button>
            <Spacer right={1} />
          </>
        )}
        <Button
          icon={!isLoading && !overlayMode && <SvgArrowForward />}
          iconPosition="right"
          isDisabled={
            !acceptedTC ||
            !captchaSuccess ||
            errorStr ||
            !allFieldsFilled ||
            isLoading
          }
          hasLoader={isLoading}
          variant="primary"
          size={buttonSize || (isMobile ? 'large' : 'medium')}
          hasFullWidth={isMobile}
          onClick={() => {
            if (!allFieldsFilled) {
              dispatch(updateLoginPlainError('All fields are required.'));

              return;
            }

            if (!emailValiate(email)) {
              dispatch(updateLoginEmailError('This email is invalid'));

              return;
            }

            const passError = hasPasswordError(password);

            if (passError) {
              dispatch(updateLoginPasswordError(passError));

              return;
            }

            const nameError = invalidUserFirstName(firstName);

            if (nameError) {
              dispatch(updateLoginPlainError(nameError));

              return;
            }

            if (password !== confirmPassword) {
              dispatch(
                updateLoginPasswordError('Oops! The passwords do not match.')
              );

              return;
            }

            dispatch(
              signUpUserCreator({
                email,
                password,
                firstName,
                lastName,
                redirectURL: window.location.href, // reload the page
                termsAccepted: acceptedTC,
              })
            );

            analytics.track(EMAIL_SIGNUP_BUTTON_CLICKED);
          }}
        >
          {overlayMode ? 'Sign Up' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}

export default EmailSignupForm;
